import React from 'react'
import about from '../assets/about.svg'
import { LuMapPin } from 'react-icons/lu'
import { useBranches } from '../hooks/useBranches'
import { useState } from 'react'
import { toast } from 'react-toastify'


const About = () => {
  const { branches, loading, error } = useBranches();
  
  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Show loading state while branches are being fetched
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 via-white to-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading page...</p>
        </div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create mailto link with form data
      const recipients = 'kissajasper@gmail.com,jasperlawrence@jasperkissa.com';
      const subject = encodeURIComponent(formData.subject || 'Contact Form Submission');
      const body = encodeURIComponent(
        `Name: ${formData.name}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone || 'Not provided'}\n\n` +
        `Message:\n${formData.message}`
      );

      // Open user's email client
      window.location.href = `mailto:${recipients}?subject=${subject}&body=${body}`;
      
      toast.success('Opening your email client...');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      toast.error('Failed to open email client');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='w-full h-fit flex flex-col relative bg-white'>
      {/* Hero Section */}
      <div className="flex flex-col items-center justify-start w-full gap-y-6 p-8 md:p-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="text-center max-w-2xl">
          <div className="text-amber-600 text-sm font-bold uppercase tracking-widest mb-4">Our Story</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            About Jasper Kissa
          </h1>
          <p className="text-lg text-gray-700 leading-relaxed">
            Since 2006, we've been your trusted partner in technology solutions, dedicated to empowering clients with innovative technology and exceptional support.
          </p>
        </div>
      </div>

      {/* About Us Content */}
      <div className="w-full py-20 px-8 md:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col gap-6">
              <div>
                <div className="text-amber-600 text-sm font-bold uppercase tracking-widest mb-3">Who We Are</div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Your Trusted Partner in Technology Solutions
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                Founded in 2006, Jasper Kissa Computer Center has rapidly become a leader in IT solutions, dedicated to empowering clients with innovative technology and exceptional support. Our mission is to ensure customer satisfaction through personalized service.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                At Jasper Kissa Computer Center, we believe technology should enhance your life, not complicate it. Our journey began with a simple vision: to bring reliable, affordable, and expert computer services to our community. Today, with multiple branches, we're proud to be a trusted name in tech solutions.
              </p>
              
              <div className="grid grid-cols-3 gap-4 pt-4">
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-6 border border-amber-100">
                  <div className="text-3xl font-bold text-amber-600 mb-2">18+</div>
                  <div className="text-sm text-gray-700 font-medium">Years of Experience</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-6 border border-amber-100">
                  <div className="text-3xl font-bold text-amber-600 mb-2">5+</div>
                  <div className="text-sm text-gray-700 font-medium">Branch Locations</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-6 border border-amber-100">
                  <div className="text-3xl font-bold text-amber-600 mb-2">10K+</div>
                  <div className="text-sm text-gray-700 font-medium">Happy Customers</div>
                </div>
              </div>
            </div>
            <div className="hidden md:flex">
              <img src={about} alt="about" className='w-full rounded-xl shadow-lg border border-gray-200'/>
            </div>
          </div>
        </div>
      </div>

      {/* Branches Section */}
      <div className="w-full bg-gradient-to-b from-slate-50 to-white py-20 px-8 md:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-amber-600 text-sm font-bold uppercase tracking-widest mb-4">Visit Us</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Come Visit Our Branches
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Discover an extensive selection of computer components at our branches. From gaming rigs to workstation builds, find everything you need to create the perfect system.
            </p>
          </div>

          {/* Branches Grid */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-600 mb-4"></div>
              <p className="text-gray-600 text-lg">Loading branches...</p>
            </div>
          )}
          
          {error && (
            <div className="text-center py-12">
              <p className="text-red-500 text-lg">{error}</p>
            </div>
          )}
          
          {!loading && !error && branches.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No branches available at the moment.</p>
            </div>
          )}
          
          {!loading && !error && branches.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {branches.map((branch) => (
                <div key={branch.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden group">
                  <div className="p-8">
                    <a href={branch.mapUrl} target='_blank' rel='noreferrer'>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-amber-600 transition-colors duration-300 flex items-center gap-3">
                        <LuMapPin size={24} className="text-amber-600" />
                        {branch.name}
                      </h3>
                    </a>
                    
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="text-gray-500 mt-1">📍</div>
                        <div>
                          <p className="text-sm font-semibold text-gray-700">Address</p>
                          <p className="text-gray-600">{branch.address}</p>
                        </div>
                      </div>
                      
                      {branch.contactNumber && (
                        <div className="flex items-start gap-3">
                          <div className="text-gray-500 mt-1">📞</div>
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Phone</p>
                            <a href={`tel:${branch.contactNumber}`} className="text-amber-600 hover:text-amber-700 font-medium">
                              {branch.contactNumber}
                            </a>
                          </div>
                        </div>
                      )}
                      
                      <a href={branch.mapUrl} target='_blank' rel='noreferrer' className="inline-block mt-6 px-6 py-2.5 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg font-semibold hover:from-amber-700 hover:to-amber-800 transition-all duration-300">
                        View on Maps →
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Information Grid */}
      <div className="w-full bg-white py-20 px-8 md:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Opening Hours */}
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-8 border border-amber-100">
              <div className="text-amber-600 text-sm font-bold uppercase tracking-widest mb-4">Hours</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Opening Hours</h3>
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-gray-900">Monday - Saturday</p>
                  <p className="text-gray-700">8:00 AM - 6:00 PM (PST)</p>
                </div>
                <div className="pt-4 border-t border-amber-200">
                  <p className="text-sm text-gray-600 italic">We're closed on Sundays and public holidays</p>
                </div>
              </div>
            </div>

            {/* Email & Social */}
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-8 border border-amber-100">
              <div className="text-amber-600 text-sm font-bold uppercase tracking-widest mb-4">Contact</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Get In Touch</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Email</p>
                  <a href="mailto:kissajasper@gmail.com" className='text-amber-600 hover:text-amber-700 font-medium transition-colors'>
                    kissajasper@gmail.com
                  </a>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Facebook</p>
                  <a href='https://www.facebook.com/JKCCofficial' target='_blank' rel='noreferrer' className="text-amber-600 hover:text-amber-700 font-medium transition-colors">
                    facebook.com/JKCCofficial
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-8 border border-amber-100">
              <div className="text-amber-600 text-sm font-bold uppercase tracking-widest mb-4">Values</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Us</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 text-lg font-bold">✓</span>
                  <span className="text-gray-700">Expert Technicians</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 text-lg font-bold">✓</span>
                  <span className="text-gray-700">Quality Guaranteed</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 text-lg font-bold">✓</span>
                  <span className="text-gray-700">24/7 Support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="w-full bg-gradient-to-b from-slate-50 to-white py-20 px-8 md:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-amber-600 text-sm font-bold uppercase tracking-widest mb-4">Get In Touch</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Send Us a Message
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Have a question or want to learn more? Fill out the form below and we'll get back to you as soon as possible.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name Field */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="text-sm font-semibold text-gray-900">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-colors duration-200"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email Field */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-sm font-semibold text-gray-900">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-colors duration-200"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Phone Field */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="phone" className="text-sm font-semibold text-gray-900">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-colors duration-200"
                    placeholder="+63 912 345 6789"
                  />
                </div>

                {/* Subject Field */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="subject" className="text-sm font-semibold text-gray-900">
                    Subject (Optional)
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-colors duration-200"
                    placeholder="PC Repair Inquiry"
                  />
                </div>
              </div>

              {/* Message Field */}
              <div className="flex flex-col gap-2">
                <label htmlFor="message" className="text-sm font-semibold text-gray-900">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none transition-colors duration-200"
                  placeholder="Tell us how we can help you..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex flex-col gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-3.5 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all shadow-md hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                <p className="text-xs text-gray-600 text-center">
                  This will open your default email client to send your message to our team.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default About